// Painel Super Master > Empresas — CRUD de `companies` (nome, domínio
// próprio do cliente, status ativo/suspenso). Atrás de platformAuthGuard no
// backend (companies.controller.ts) — só acessível autenticado como
// PlatformAdmin, nunca por um User de empresa comum.
import { platformGet, platformPatch, platformPost } from "@/lib/api/gpsdrive-platform-client";

export type CompanyStatus = "active" | "suspended";

// Agenda/Câmera (pedido 2026-09-16) — ativados pelo Super Master no
// próprio formulário de criação/edição da empresa (ver company-modules
// no backend). "agenda" default true (liberado pra toda empresa até
// 2026-09-16), "camera" default false (módulo novo, opt-in).
export interface CompanyModules {
  agenda: boolean;
  camera: boolean;
  // Gestão de Frota (2026-10-01) — opt-in, default false.
  frota: boolean;
}

export interface Company {
  id: string;
  name: string;
  domain: string | null;
  status: CompanyStatus;
  // Quanto esta empresa paga à GPS Drive por veículo rastreado — null
  // enquanto o super master ainda não precificou. Nunca confundir com
  // "Plan" (Administração > Planos), que é a empresa cobrando os próprios
  // clientes finais.
  pricePerVehicle: number | null;
  vehicleCount: number;
  modules: CompanyModules;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCompanyInput {
  name: string;
  domain?: string | null;
  modules?: Partial<CompanyModules>;
}

export interface UpdateCompanyInput {
  name?: string;
  domain?: string | null;
  status?: CompanyStatus;
  pricePerVehicle?: number | null;
  modules?: Partial<CompanyModules>;
}

// Super Master → empresa → Administradores (2026-10-16). No rastreamento o
// admin nasce GERENTE (nunca administrador geral), ver backend
// CompaniesService#createAdmin.
export interface CompanyAdmin {
  id: string;
  name: string;
  email: string;
  traccarUserId: number | null;
  createdAt: string;
}

export interface CreateCompanyAdminInput {
  name: string;
  email: string;
  password: string;
  phone?: string;
}

// Super Master → Empresa → Domínio (fase 5, 2026-10-17) — ver
// company-domain.service.ts no backend.
export type CompanyDomainStatus = "sem_dominio" | "aguardando_dns" | "dns_ok" | "ativando" | "ativo" | "erro";

export interface CompanyDomainInfo {
  domain: string | null;
  status: CompanyDomainStatus;
  record: { type: "A"; name: string; zone: string; value: string; ttl: number } | null;
  dns: { checkedAt: string; resolved: string[]; ipv6: string[]; ok: boolean; message: string } | null;
  lastJob: { id: string; status: string; output: string | null; createdAt: string; finishedAt: string | null } | null;
  url: string | null;
}

export const gpsDriveCompaniesService = {
  getDomain: (id: string) => platformGet<CompanyDomainInfo>(`/api/v1/companies/${id}/domain`),
  activateDomain: (id: string) => platformPost<CompanyDomainInfo>(`/api/v1/companies/${id}/domain/activate`),
  listAdmins: (id: string) => platformGet<CompanyAdmin[]>(`/api/v1/companies/${id}/admins`),
  createAdmin: (id: string, input: CreateCompanyAdminInput) =>
    platformPost<CompanyAdmin>(`/api/v1/companies/${id}/admins`, input),
  list: () => platformGet<Company[]>("/api/v1/companies"),
  get: (id: string) => platformGet<Company>(`/api/v1/companies/${id}`),
  create: (input: CreateCompanyInput) => platformPost<Company>("/api/v1/companies", input),
  update: (id: string, input: UpdateCompanyInput) =>
    platformPatch<Company>(`/api/v1/companies/${id}`, input),
};
